/**
 * Booking Detail Page
 *
 * Displays booking details with the ability to cancel.
 * Similar to confirmation page but for viewing existing bookings.
 *
 * Route: /bookings/:bookingId?tenant-handle=<handle>&club-id=<id>
 *
 * Data Flow:
 * - Fetches booking by ID (requires auth)
 * - Fetches payment method details separately for displaying last 4 digits
 *
 * Auth Requirements:
 * - Protected route (requires authentication)
 * - Who may see it is the API's answer: the booker or a participant (`ownedByUser`) —
 *   payment rows are already the viewer's share ("Paid by your team").
 *
 * @module pages/booking
 */

import { useState } from "react";
import { useParams } from "react-router";
// Never react-router's useNavigate/Link here: this app carries tenant-handle + club-id in the
// query string, and a bare path drops them — MissingParamsGuard then blocks the whole app.
import { TenantLink, useTenantNavigate } from "~/lib/navigation";
import { BottomSheet, butterToast } from "@butter/ui/patterns";
import { useAuth } from "~/services/auth";
import { useClubId } from "~/services/appRuntime";
import { useClubQuery } from "~/services/internalApi/club/hooks";
import { deriveDeferredBalance, deriveInvoiceTotalsRows, formatInvoiceMoney } from "@butter/ui/domains/billing";
import { formatPaymentTypeLabel } from "@butter/ui/domains/booking";
import { useInvoiceQuery } from "~/services/internalApi/invoice";
import { formatFeeRate } from "@butter/library/pricing";
import {
  formatClubDate,
  formatClubTime,
  formatDuration,
  asUtcDate,
  asTimezone,
} from "@butter/library/datetime";
import { resolveCancellationEligibility } from "@butter/library/cancellation";
import { useSimulatorCompanionEnabled } from "~/features/simulatorCompanion";
import { ExtendBookingRow } from "./components";
import { AccessCodesCard } from "@butter/ui/domains/access";
import { BOOKING_DETAIL_TYPE_CONFIG, COMPLIMENTARY_COVERAGE_ROW } from "./bookingDetailTypeConfig";
import {
  LESSON_PLANS_ACTION_LABEL,
  lessonCancelOutcome,
  lessonCancelledMessage,
  lessonCoverage,
  lessonRatePerHour,
  lessonRestore,
  lessonRestoreAfterCancel,
} from "@butter/library/lessons";
import {
  useBookingQuery,
  useCancelBooking,
  useCancelDemoBooking,
} from "~/services/internalApi/booking/hooks";
import { bookingService } from "~/services/internalApi/booking";
import { TicketCard, TicketDivider } from "@butter/ui/patterns";
import { CopyButton } from "@butter/ui/primitives";
import { DownloadIcon } from "@butter/ui/icons";
import { getBayImageUrl } from "@butter/ui/utils";
import { BookingSummaryDisplay } from "@butter/ui/domains/booking";
import { InstructorIdentity } from "@butter/ui/domains/lessons";
import { useLessonInstructor } from "../lessons/useLessonInstructor";
import type { ApiBookingWithRelations } from "~/services/internalApi/booking";
import { useHashModal } from "~/hooks/useHashModal";
import { phoneForDisplay } from "@butter/library/phone";

/**
 * Booking Detail Page Component
 */
export function BookingDetailPage() {
  const navigate = useTenantNavigate();
  const { bookingId: bookingIdParam } = useParams<{ bookingId: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [clubId] = useClubId();
  const clubIdNumber = clubId ? Number(clubId) : null;
  const [showContactClubModal, setShowContactClubModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);

  // Extract booking ID from URL params
  const bookingIdNum = bookingIdParam ? parseInt(bookingIdParam, 10) : null;

  // Opens the global SignInModal rendered by MainLayout (same hash key).
  const signInModal = useHashModal("dialog-sign-in");

  // Club data for the "contact the club" cancel path (phone number).
  const { data: club } = useClubQuery();
  const clubPhone = phoneForDisplay(club?.phone, club?.country);
  // The club's SimLock switch — the extend offer shows only while it is on (the Bay PIN's rule
  // is the API's, in the `access` block).
  const companionEnabled = useSimulatorCompanionEnabled();

  // Fetch booking by ID
  const {
    data: booking,
    isLoading: isLoadingBooking,
    error: bookingError,
  } = useBookingQuery({
    bookingId: bookingIdNum,
    // Only fetch once authenticated — the endpoint 401s for guests. When auth flips
    // true post-login, React Query auto-enables and refetches.
    enabled: isAuthenticated && bookingIdNum !== null,
  });

  // The booking's invoice itemizes the processing fee, its D63 adjustment and the account credit
  // that paid part of the booking — Total Paid is the card's share. `invoiceId` is present only
  // for the booker. The invoice decides whether a fee applies, not the club's setting today.
  const invoiceId = booking?.invoiceId ?? null;
  const { invoice: fetchedInvoice, isLoading: invoiceLoading } = useInvoiceQuery({
    invoiceId,
    enabled: isAuthenticated && invoiceId !== null,
  });
  // A voided or cancelled invoice is not a receipt: its fees were never collected (a waived
  // booking's card fee would otherwise sit above a $0 total).
  const writtenOff = fetchedInvoice?.status === 'voided' || fetchedInvoice?.status === 'cancelled';
  const receiptRows = fetchedInvoice && !writtenOff ? deriveInvoiceTotalsRows(fetchedInvoice.totals) : null;

  // Cancel booking mutation
  // Note: clubId is required for proper query invalidation
  // We derive clubId from the booking data (most reliable) or from the URL
  const cancellationClubId = booking?.clubId ?? clubIdNumber;
  
  // Per-type behavior — ONE details route, data-driven differences (S3).
  const typeConfig = BOOKING_DETAIL_TYPE_CONFIG[booking?.type ?? 'standard'] ?? BOOKING_DETAIL_TYPE_CONFIG.standard;
  // Any booking but a demo names its own payment type — "Pass", "Team", "Lesson" — rather than
  // calling every covered booking "Membership Coverage". A demo keeps its own row.
  const coverageRow =
    booking?.paymentType === 'comp'
      ? COMPLIMENTARY_COVERAGE_ROW
      : booking?.type !== 'demo' && booking?.paymentType
        ? { ...typeConfig.coverageRow, label: formatPaymentTypeLabel(booking.paymentType) }
        : typeConfig.coverageRow;
  // The club deferred this booking's fees: nothing collected yet, the member owes the invoice.
  const deferredBalance = deriveDeferredBalance(booking, fetchedInvoice);
  // A pending booking with an invoice may be deferred — don't flash "$0.00" while it loads.
  // Only WHILE it loads: a failed read falls back to the paid figure, as it did before.
  const awaitingInvoice = booking?.paymentStatus === 'pending' && invoiceId !== null && invoiceLoading;

  const { mutate: cancelDemoBookingMutate, isPending: isCancellingDemo } = useCancelDemoBooking({
    clubId: cancellationClubId ?? 0, // callers only invoke when a clubId is valid — same contract as useCancelBooking
    onSuccess: () => {
      butterToast.success("Your demo has been cancelled.", typeConfig.cancelledToastTitle);
      navigate("/");
    },
    onError: (error) => {
      butterToast.error(error, "Couldn't cancel demo");
    },
  });

  const { mutate: cancelBooking, isPending: isCancellingStandard } = useCancelBooking({
    clubId: cancellationClubId ?? 0, // TypeScript requires a value; callers must only invoke cancelBooking when clubId is valid
    onSuccess: (data) => {
      // Money and minutes both come from the server. The confirm sheet had to predict
      // the minutes; the toast reports what the cancel actually restored.
      const { creditedAmountCents } = data;
      const restore = booking ? lessonRestoreAfterCancel(booking, data) : null;
      const credited = `$${(creditedAmountCents / 100).toFixed(2)} has been credited to your account`;
      const message = restore
        ? lessonCancelledMessage(restore, creditedAmountCents > 0 ? `$${(creditedAmountCents / 100).toFixed(2)}` : null)
        : creditedAmountCents > 0
          ? `${credited}.`
          : "Booking cancelled successfully";
      // A package moved (or is unlimited): let the member see the balance it now shows.
      butterToast.success(
        message,
        typeConfig.cancelledToastTitle,
        restore ? { action: { label: LESSON_PLANS_ACTION_LABEL, onClick: () => navigate("/plans") } } : undefined,
      );
      // Lessons stay here: the detail refetches as cancelled and shows the restored hours.
      if (booking?.type !== "lesson") navigate("/");
    },
    onError: (error) => {
      // Permanent "contact the club" cases (spec §3: expired pass, no home club, a sale that cannot be reversed) are
      // flagged by the server with contactRequired. Branch on that, NOT the message —
      // retryable 500s ("…please try again or contact your club") also contain "contact".
      if ((error as { contactRequired?: boolean }).contactRequired) {
        setShowContactClubModal(true);
      } else {
        // Show error toast notification
        butterToast.error(error, "Couldn't cancel booking");
      }
    },
  });

  const isCancelling = isCancellingStandard || isCancellingDemo;

  const isLoading = authLoading || isLoadingBooking;

  // Derive error state from booking fetch error
  const errorMessage = bookingError
    ? bookingError.message || "Failed to load booking details"
    : null;

  // Handle cancel booking
  const handleCancelBooking = () => {
    if (!booking) return;
    
    // Safety check: Prevent cancellation if we don't have a valid clubId
    // This ensures cache invalidation will target the correct club
    if (!cancellationClubId) {
      butterToast.error("Club information isn't available yet.", "Couldn't cancel booking");
      return;
    }

    // Every cancellable booking self-cancels here, regardless of how it was paid for —
    // the server converts real money to account credit (it never touches the card), so a
    // direct-pay booking has nothing extra to arrange. An earlier club policy routed
    // direct-pay to "contact the club"; that predated the credit path and disagreed with
    // both the API and mobile-app. The contact-club modal is still reached, but only where
    // the SERVER says it must be (`contactRequired`: expired pass, no home club, an unreversible sale).
    setShowCancelConfirmModal(true);
  };

  // Confirm and execute cancellation
  const confirmCancelBooking = () => {
    if (!booking) return;
    
    // Safety check: Prevent cancellation if we don't have a valid clubId
    if (!cancellationClubId) {
      butterToast.error("Club information isn't available yet.", "Couldn't cancel booking");
      return;
    }

    // Close the confirmation modal
    setShowCancelConfirmModal(false);

    // Call the type's cancel endpoint — a demo goes through the demo op
    // (member-scoped, no money machinery); everything else the standard path.
    if (typeConfig.cancelKind === 'demo') {
      cancelDemoBookingMutate(booking.id);
    } else {
      cancelBooking(booking.id);
    }
  };

  /**
   * Handle download receipt button click
   * Downloads the booking receipt PDF via bookingService
   */
  const handleDownloadReceipt = async () => {
    if (!booking) return;

    try {
      // Use the booking service (follows internal API pattern)
      const result = await bookingService.downloadReceipt(booking.id);

      if (!result.success) {
        console.error("[downloadReceipt] Error:", result.message);
        butterToast(result, "Couldn’t download receipt");
        return;
      }

      // Create a download link
      const url = window.URL.createObjectURL(result.payload);
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt-booking-${booking.id}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);

      butterToast.success("Your receipt has been downloaded.", "Receipt downloaded");
    } catch (error) {
      console.error("[downloadReceipt] Error:", error);
      butterToast.error("Please try again.", "Couldn’t download receipt");
    }
  };

  if (!bookingIdNum) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Invalid Booking
          </h1>
          <p className="text-gray-600 mb-6">No booking ID provided.</p>
          <TenantLink
            to="/"
            className="inline-flex items-center px-6 py-2 bg-theme-600 text-white rounded-full font-medium hover:bg-theme-700"
          >
            Go Home
          </TenantLink>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !booking) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        data-testid="booking-detail-signin-required"
      >
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Sign in Required</h1>
          <p className="text-gray-600 mb-6">
            Please sign in to view your booking details.
          </p>
          <button
            onClick={signInModal.open}
            className="px-6 py-2 bg-theme-600 text-white rounded-full font-medium text-interactive:bg-theme-700"
            aria-label="Sign in to view your booking"
            data-testid="booking-detail-signin-btn"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <TenantLink
            to="/"
            className="inline-flex items-center px-6 py-2 bg-theme-600 text-white rounded-full font-medium hover:bg-theme-700"
          >
            Go Home
          </TenantLink>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Booking Not Found
          </h1>
          <p className="text-gray-600 mb-6">We couldn't find this booking.</p>
          <TenantLink
            to="/"
            className="inline-flex items-center px-6 py-2 bg-theme-600 text-white rounded-full font-medium hover:bg-theme-700"
          >
            Go Home
          </TenantLink>
        </div>
      </div>
    );
  }

  // Calculate duration
  const startTime = asUtcDate(new Date(booking.startTime));
  const endTime = asUtcDate(new Date(booking.endTime));
  const durationMs = endTime.getTime() - startTime.getTime();
  const durationMinutes = Math.floor(durationMs / (1000 * 60));

  // Shared with the server, not re-derived: `resolveCancellationEligibility` is the SAME
  // function the public cancel endpoint gates on, so this button can never be offered for a
  // booking the API will reject. Without it the button renders on an ended booking and the
  // member gets a 400. A check-in is the one refusal that gets a sentence: the member may
  // have forgotten they are sitting in the booking.
  const eligibility = resolveCancellationEligibility(
    { status: booking.status, attendance: booking.attendance, startTime, endTime },
    asUtcDate(new Date()),
  );
  const canCancel = eligibility.allowed;
  const checkedInNote = !eligibility.allowed && eligibility.reason === "checked_in" ? eligibility.message : null;

  // What the member gets back, predicted so the confirmation modal can say it BEFORE they
  // commit — the success toast reports the credit only once the booking is already gone.
  //
  // Mirrors the server's own rules rather than guessing from `paymentType` names:
  // money is credited only when it was actually CAPTURED (`hadCapturedMoney` in
  // `cancelBooking.ts` — amount > 0 AND paymentStatus 'completed'), and pass/membership
  // allowance frees up for subscription-backed bookings. The paymentStatus half is
  // load-bearing: a `pending` booking carries a non-zero amount and credits nothing, so
  // keying on the amount alone would promise money the member never receives.
  //
  // This is a prediction, not a guarantee — the server is authoritative and can still route
  // an expired pass to "contact the club" after the member confirms.
  // `viewerPaidAmount` is THREE-valued and `??` cannot read it: `undefined` means an
  // older server never sent the field, while an explicit `null` means this member paid
  // nothing — which is the whole point of the field, and exactly the case `??` would
  // collapse back onto the booking's aggregate.
  const viewerPaid =
    booking.viewerPaidAmount === undefined
      ? Number(booking.paymentAmount) || 0
      : Number(booking.viewerPaidAmount ?? 0);
  /** Someone else's money paid for this booking, and none of it was theirs. */
  const paidByOthers =
    Number(booking.paymentAmount) > 0 && booking.viewerPaidAmount === null;
  // The account-credit part of what they paid, and so what the card took (S1-D10): the receipt's
  // "Total Paid" is the card's share, under an "Account Credit" row. An older server sends no
  // field, and the receipt keeps today's whole figure with the first invoice's credit row.
  const viewerCredit = Number(booking.viewerCreditPaidAmount ?? 0);
  const viewerCardPaid = Math.max(0, viewerPaid - viewerCredit);
  const creditRowAmount = booking.viewerCreditPaidAmount !== undefined
    ? viewerCredit
    : receiptRows?.hasCredit ? fetchedInvoice?.totals.creditApplied ?? 0 : 0;
  // Anything paid at all — card or credit. The column is the card share only, so it alone would
  // hide a receipt (or an overage) that account credit paid in full.
  const paidAnything = viewerPaid > 0 || Number(booking.paymentAmount) > 0;
  // Package-backed lessons only; a direct lesson is priced below (Rate / Subtotal / Total).
  const lessonCover = lessonCoverage(
    booking,
    paidAnything ? (paidByOthers ? "Paid by your team" : `$${viewerPaid.toFixed(2)}`) : null,
  );
  const isLessonDirect = booking.paymentType === "lesson_direct";
  // Invoice subtotal when loaded, else the viewer's own charge (never a figure we invent).
  const lessonSubtotal = !isLessonDirect || paidByOthers
    ? null
    : fetchedInvoice ? fetchedInvoice.totals.subtotal : awaitingInvoice ? null : viewerPaid;
  // Rate per hour renders only once the backend sends the instructor's rate; never inferred from a charge.
  const lessonRate = isLessonDirect ? lessonRatePerHour() : null;

  const cancelOutcome = (() => {
    // A demo held no money and consumed no allowance — nothing to predict.
    if (!typeConfig.predictsCancelOutcome) return undefined;
    const isPass = booking.paymentType.startsWith("pass");
    const isMembership = booking.paymentType.startsWith("membership");

    // Paid for, but NOT by them — a team booking whose overage the leads covered.
    // Checked first, and before any credit is promised: this member has no money
    // coming back, so a figure here would be somebody else's.
    if (paidByOthers) {
      if (isPass || isMembership) return "Your usage will be reversed.";
      return undefined;
    }

    // The VIEWER's own money, never the booking's total.
    const amount = viewerPaid;
    const willCredit = amount > 0 && booking.paymentStatus === "completed";
    const credit = `$${amount.toFixed(2)}`;

    // The lesson axis keys on the entitlement FK, and is tested FIRST: a `lesson_direct`
    // booking has BOTH lesson fields null, so an FK test is the only thing that tells it
    // from a covered one. Pass/membership below still key on the paymentType prefix —
    // their legacy rows predate the FK columns and would lose their copy.
    const restore = lessonRestore(booking);
    if (restore) return lessonCancelOutcome(restore, willCredit ? credit : null);

    if (willCredit && (isPass || isMembership))
      return `Your account will be credited ${credit} and your usage reversed.`;
    if (willCredit) return `Your account will be credited ${credit}.`;
    if (isPass) return "Your pass usage will be reversed.";
    if (isMembership) return "Your membership usage will be reversed.";
    // `comp`, `team`, or an uncaptured booking — nothing to hand back, so say nothing rather
    // than pad the modal with filler.
    return undefined;
  })();

  // Format date and time in club timezone. The shared API type carries timezone as a
  // plain string; re-brand it for the datetime helpers.
  const timezone = asTimezone(booking.club!.timezone);
  const formattedDate = formatClubDate(
    startTime,
    timezone,
    "EEEE, d MMMM, yyyy"
  );

  // Format times using datetime library utility
  const startTimeStr = formatClubTime(startTime, timezone);
  const endTimeStr = formatClubTime(endTime, timezone);
  const timeRange = `${startTimeStr} - ${endTimeStr}`;

  // Format duration
  const formattedDuration = formatDuration(durationMinutes);

  // Bay name
  const bayName = booking.bay?.name || `Bay ${booking.bayId}`;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
      {/* Page Title */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900">My Booking</h1>
        <p className="text-base text-gray-600 mt-2">
          View and manage your booking
        </p>
      </div>

      {/* Ticket Card with Booking Summary and Details */}
      <TicketCard className="mb-6" punchRadius="1.3rem" borderColor="var(--theme-100)">
        {/* Top Section: Booking Summary */}
        <div className="p-6">
          {booking.type === "lesson" && <BookingInstructorIdentity booking={booking} />}
          <BookingSummaryDisplay
            bayName={bayName}
            date={formattedDate}
            timeRange={timeRange}
            duration={formattedDuration}
          />
        </div>

        {/* Divider */}
        <TicketDivider punch />

        {/* Bottom Section: Bay Image (Left) and Booking Details (Right) */}
        <section aria-label="Booking details" className="p-6">
          {/* Two Column Grid: Image Left, Details Right */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column: Bay Image */}
            <div className="relative w-full">
              <img
                src={getBayImageUrl(
                  booking.bay || { image: null },
                  booking.bayId
                )}
                alt={bayName}
                className="w-full h-full object-cover rounded-2xl"
                style={{ aspectRatio: "16/9" }}
              />
              {booking.status === "cancelled" && (
                <span
                  data-testid="booking-status-cancelled"
                  className="absolute right-3 top-3 rounded-full bg-white/90 px-2.5 py-1.5 text-xs font-medium leading-3 text-butter-error-500 backdrop-blur-sm"
                >
                  Cancelled
                </span>
              )}
              {/* The club released the bay when nobody checked in (simulator-companion D68). */}
              {booking.status === "no-show" && (
                <span
                  data-testid="booking-status-no-show"
                  className="absolute right-3 top-3 rounded-full bg-white/90 px-2.5 py-1.5 text-xs font-medium leading-3 text-butter-error-500 backdrop-blur-sm"
                >
                  No-show
                </span>
              )}
            </div>

            {/* Right Column: Booking Details */}
            <div className="space-y-4">
              {/* Booking ID */}
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium text-fg-quaternary">
                  Booking ID
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-fg-primary">
                    {booking.id}
                  </span>
                  <CopyButton
                    text={booking.id.toString()}
                    size="default"
                    onCopySuccess={() => butterToast.success("Copied to clipboard")}
                    onCopyError={() => butterToast.error("Failed to copy")}
                  />
                </div>
              </div>

              {isLessonDirect && lessonRate !== null && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-fg-quaternary">Rate per hour</span>
                  <span className="text-sm font-semibold text-fg-primary">{formatInvoiceMoney(lessonRate)}</span>
                </div>
              )}
              {isLessonDirect && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-fg-quaternary">Subtotal</span>
                  <span className="text-sm font-semibold text-fg-primary">
                    {awaitingInvoice ? "—" : paidByOthers ? "Paid by your team" : formatInvoiceMoney(lessonSubtotal ?? 0)}
                  </span>
                </div>
              )}

              {/* Fee → adjustment → credit, above Total Paid — the order InvoiceTotals uses. */}
              {fetchedInvoice && receiptRows?.hasProcessingFee && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-fg-quaternary">
                    Processing Fee{fetchedInvoice.totals.processingFeeRate ? ` (${formatFeeRate(fetchedInvoice.totals.processingFeeRate)})` : ""}
                  </span>
                  <span className="text-sm font-semibold text-fg-primary">
                    {formatInvoiceMoney(fetchedInvoice.totals.processingFee)}
                  </span>
                </div>
              )}
              {fetchedInvoice && receiptRows?.hasAdjustment && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-fg-quaternary">
                    Processing Fee Adjustment
                  </span>
                  <span className="text-sm font-semibold text-fg-primary">
                    {formatInvoiceMoney(fetchedInvoice.totals.adjustmentTotal)}
                  </span>
                </div>
              )}
              {creditRowAmount > 0 && !paidByOthers && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-fg-quaternary">
                    Account Credit
                  </span>
                  <span className="text-sm font-semibold text-fg-primary" data-testid="booking-account-credit">
                    {formatInvoiceMoney(-creditRowAmount)}
                  </span>
                </div>
              )}

              {/* Payment: direct bookings show the amount paid; membership/pass
                  bookings are covered with no charge; a comp says the club waived it */}
              <div className="flex items-center justify-between py-2">
                {deferredBalance ? (
                  /* Nothing collected yet — the club takes it at the counter. */
                  <>
                    <span className="text-sm font-medium text-fg-quaternary" data-testid="booking-amount-due">
                      Amount due
                      <span className="block text-xs">Pay at the club</span>
                    </span>
                    <span className="text-sm font-semibold text-fg-primary">
                      {formatInvoiceMoney(deferredBalance.amountDue)}
                    </span>
                  </>
                ) : lessonCover ? (
                  <div className="w-full space-y-2">
                    <div className="space-y-4">
                      {lessonCover.rows.map((row) => (
                        <div key={row.label} className="flex items-center justify-between">
                          <span className="text-sm font-medium text-fg-quaternary">{row.label}</span>
                          <span className="text-sm font-semibold text-fg-primary">{row.value}</span>
                        </div>
                      ))}
                    </div>
                    {lessonCover.note && (
                      <p className="text-sm font-medium text-fg-quaternary" data-testid="booking-lesson-coverage-note">
                        {lessonCover.note}
                      </p>
                    )}
                  </div>
                ) : isLessonDirect ? (
                  <>
                    <span className="text-sm font-medium text-fg-quaternary">Total</span>
                    <span className="text-sm font-semibold text-fg-primary">
                      {awaitingInvoice ? "—" : paidByOthers ? "Paid by your team" : `$${viewerCardPaid.toFixed(2)}`}
                    </span>
                  </>
                ) : booking.paymentType === "direct" ? (
                  <>
                    <span className="text-sm font-medium text-fg-quaternary">
                      Total Paid
                    </span>
                    <span className="text-sm font-semibold text-fg-primary" data-testid="booking-total-paid">
                      {awaitingInvoice ? "—" : paidByOthers ? "Paid by your team" : `$${viewerCardPaid.toFixed(2)}`}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-sm font-medium text-fg-quaternary">
                      {coverageRow.label}
                    </span>
                    {/* A plan booking that still cost something — an overage, guest fees — says
                        what was paid, not "no payment required" (mobile's rule). */}
                    <span className="text-sm font-semibold text-fg-primary">
                      {paidAnything
                        ? paidByOthers ? "Paid by your team" : `$${viewerCardPaid.toFixed(2)}`
                        : coverageRow.value}
                    </span>
                  </>
                )}
              </div>

              {/* Access Codes (simulator-companion D77): the door code and the Bay PIN, each
                  with when it starts working — both decided by the API (the door code only while
                  the club's provider is a keypad one, accessControl KI #6). */}
              <AccessCodesCard
                variant="rows"
                testIdPrefix="booking"
                doorCode={booking.access?.doorCode ? { code: booking.access.doorCode, worksFrom: booking.access.doorOpensAtLabel } : null}
                bayPin={booking.access?.bayPin ? { code: booking.access.bayPin.code, worksFrom: booking.access.bayPin.opensAtLabel } : null}
              />
              {/* One more block, mid-session (Simulator Companion slice 9). The row gates itself. */}
              <ExtendBookingRow booking={booking} enabled={companionEnabled} timezone={booking.club?.timezone} testId="booking-extend" />

              {/* Download Receipt (inline) — shown whenever a payment was made
                  (direct pay or membership overage), not for fully-covered bookings.
                  A receipt is the viewer's own invoice: a participant whose team paid
                  has none, so the button would only fail. */}
              {paidAnything && !paidByOthers && (
                <div className="border-t pt-4">
                  <button
                    onClick={handleDownloadReceipt}
                    className="flex w-full items-center justify-center gap-2 py-3 text-sm font-medium text-fg-primary transition-colors hover:text-fg-tertiary focus:outline-none focus-visible:underline sm:justify-end"
                    aria-label="Download receipt"
                  >
                    <DownloadIcon className="size-5" ariaHidden />
                    Download Receipt
                  </button>
                </div>
              )}

              {/* Checked in: no Cancel, and one line saying why. */}
              {checkedInNote && (
                <p className="text-sm text-fg-quaternary sm:text-right" data-testid="booking-checked-in-note">
                  {checkedInNote}
                </p>
              )}

              {/* Cancel Button — every payment type self-cancels here, matching mobile.
                  `canCancel` is the shared @butter/library/cancellation gate the API
                  enforces, so a completed/past booking shows no cancel at all. */}
              {canCancel && (
                <div className="flex justify-end">
                  <button
                    onClick={handleCancelBooking}
                    disabled={isCancelling}
                    data-testid="booking-cancel-button"
                    className="w-full rounded-full border border-theme-600 bg-white px-8 py-3 font-medium text-fg-primary transition-colors hover:bg-theme-50 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                  >
                    {isCancelling ? "Cancelling..." : "Cancel"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </TicketCard>

      {/* Cancel confirmation — every payment type; money comes back as account credit */}
      <BottomSheet
        open={showCancelConfirmModal}
        onOpenChange={setShowCancelConfirmModal}
        title={typeConfig.cancelSheetTitle}
        description={cancelOutcome}
        isBusy={isCancelling}
        className="mx-auto max-w-lg"
      >
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={confirmCancelBooking}
            disabled={isCancelling}
            data-testid="booking-cancel-confirm-button"
            className="btn btn-destructive btn-xl flex-1"
          >
            {isCancelling ? "Cancelling..." : "Cancel Booking"}
          </button>
          <button
            type="button"
            onClick={() => setShowCancelConfirmModal(false)}
            disabled={isCancelling}
            data-testid="booking-cancel-keep-button"
            className="btn btn-outline btn-xl flex-1"
            autoFocus
          >
            No, Keep It
          </button>
        </div>
      </BottomSheet>

      {/* Contact Club Modal — server-driven only (`contactRequired`: expired pass, no home club, an unreversible sale) */}
      {showContactClubModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6">
            <h2 className="mb-1 text-xl font-bold text-gray-900">
              Need to cancel?
            </h2>
            <p className="mb-4 text-sm text-gray-500">
              To cancel this booking, contact the club owner.
            </p>
            {clubPhone.label && (
              <p className="mb-6 flex items-center gap-2 text-sm text-gray-700">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className="size-4 shrink-0 text-gray-500"
                  aria-hidden="true"
                >
                  <path
                    d="M15.5 21C7.5 21 3 16.5 3 8.5 3 7.67 3.67 7 4.5 7h2c.6 0 1.1.4 1.2 1l.5 2.5c.1.5-.1 1-.5 1.3l-1.3 1a10 10 0 0 0 4.3 4.3l1-1.3c.3-.4.8-.6 1.3-.5l2.5.5c.6.1 1 .6 1 1.2v2c0 .83-.67 1.5-1.5 1.5Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="font-medium">Call us:</span>
                {clubPhone.telHref ? (
                  <a
                    href={clubPhone.telHref}
                    className="font-semibold text-gray-900"
                  >
                    {clubPhone.label}
                  </a>
                ) : (
                  <span className="font-semibold text-gray-900">{clubPhone.label}</span>
                )}
              </p>
            )}
            <button
              onClick={() => setShowContactClubModal(false)}
              className="w-full rounded-full border border-theme-600 px-6 py-3 text-sm font-semibold text-theme-700 transition-colors hover:bg-theme-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-600 focus-visible:ring-offset-2"
            >
              Go Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * "Lesson with" identity for a lesson booking. Mounted only for `type === 'lesson'`, so a
 * standard booking runs no lesson queries.
 */
function BookingInstructorIdentity({
  booking,
}: {
  booking: Pick<ApiBookingWithRelations, "clubId" | "staffId" | "userLessonId">;
}) {
  const { isAuthenticated } = useAuth();
  const { instructor, settled } = useLessonInstructor({
    instructorId: booking.staffId ?? null,
    clubId: booking.clubId,
    isAuthenticated,
    userLessonId: booking.userLessonId,
  });
  // Nothing until the lookups answer, so "Your instructor" never flashes before the real name.
  if (!settled) return null;
  return (
    <div data-testid="booking-detail-instructor" className="px-4 pt-6 md:px-6">
      <InstructorIdentity
        variant="compact"
        label="Lesson with"
        name={instructor?.name ?? "Your instructor"}
        image={instructor?.image ?? null}
        unresolved={instructor === null}
      />
    </div>
  );
}
